import { ETextStyleVariant, GeneralInstantBookIcon, Text } from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';

import { defaultLocale, TCurrency } from '@/config/locales';
import { PriceType } from '@/redux/selectors/listing/page';
import { formatCurrency } from '@/utility/currency';

import { ListingTilePriceDiscount } from './ListingTilePriceDiscount';

type TListingTilePriceProps = {
  instantBook?: boolean;
  showDiscount?: boolean;
  weeklyDiscountPercentage?: number;
  monthlyDiscountPercentage?: number;
  currency?: TCurrency;
  lowestPrice?: number;
  currentPrice?: number;
  originalPrice?: number;
  period?: PriceType;
  priceTextVariant?: ETextStyleVariant;
  periodTextVariant?: ETextStyleVariant;
  className?: string;
};

export const ListingTilePrice = ({
  instantBook = false,
  showDiscount = true,
  weeklyDiscountPercentage,
  monthlyDiscountPercentage,
  currency = defaultLocale.base_currency,
  lowestPrice = 0,
  currentPrice = lowestPrice,
  originalPrice = currentPrice,
  period = PriceType.NIGHT,
  priceTextVariant = ETextStyleVariant.LargeBold,
  periodTextVariant = ETextStyleVariant.MediumRegular,
  className = '',
}: TListingTilePriceProps) => {
  const intl = useIntl();

  const formattedCurrentPrice = formatCurrency({
    currency,
    digits: 2,
    priceInCents: currentPrice,
  });

  return (
    <div className={`flex items-center flex-wrap gap-1 ${className}`}>
      <div className="flex items-center flex-nowrap gap-1">
        {instantBook && (
          <GeneralInstantBookIcon
            aria-label={intl.formatMessage({
              description: '[Price]: screen reader message for instant book',
              defaultMessage: 'This listing allows instant booking.',
              id: 'ZpLmIK',
            })}
            className="w-4 h-4 text-canvas-300 shrink-0"
          />
        )}

        {showDiscount && (
          <ListingTilePriceDiscount
            weeklyDiscountPercentage={weeklyDiscountPercentage}
            monthlyDiscountPercentage={monthlyDiscountPercentage}
            originalPrice={originalPrice}
            currentPrice={currentPrice}
            period={period}
          />
        )}
      </div>

      <Text
        component="div"
        variant={priceTextVariant}
        aria-label={intl.formatMessage(
          {
            description: '[Price]: screen reader message for current price per period (day/night)',
            defaultMessage: '{currentPrice} per {period}',
            id: 'RgOVlk',
          },
          { currentPrice: formattedCurrentPrice, period },
        )}
        className="flex items-center gap-1 flex-nowrap">
        {formattedCurrentPrice}

        {period && (
          <Text component="span" variant={periodTextVariant} className="text-gray-50">
            {intl.formatMessage(
              {
                description: '[Price]: current price period for a listing',
                defaultMessage: '{period, select, day {/day} night {/night} other {}}',
                id: 'xMIO4W',
              },
              { period },
            )}
          </Text>
        )}
      </Text>
    </div>
  );
};
